import { memo, useCallback, useState } from 'react';
import clsx from 'clsx';
import type { Conversation } from '@/lib/api';
import UserAvatar from './UserAvatar';
import PresenceDot from './PresenceDot';
import NotificationPrefsMenu from './NotificationPrefsMenu';
import { timeShort } from '@/lib/format';
import { useChatStore } from '@/stores/chat';

interface Props {
  conversation: Conversation;
  active: boolean;
  onSelect: (id: string) => void;
  /**
   * The viewer's user id from `AuthContext`, used only as a fallback when the
   * chat store's `currentUserId` hasn't been synced yet (see `deriveTitle`).
   */
  selfUserId?: string | null;
}

/**
 * Compute a sensible title for the row even when the backend sends a direct
 * conversation without an explicit `name` (the API stores `name = NULL` for
 * direct chats). Direct chats conventionally show the OTHER participant's
 * display name (falling back to username).
 *
 * Note: a direct conversation row may arrive with `members: undefined` or
 * only the viewer (this happens when the backend serializer omits the
 * partner enrichment for the listing endpoint). In that case we fall back to
 * the last message's sender, then to a deterministic placeholder that
 * doesn't lie about being a name.
 *
 * The viewer's id can come from two places: the chat store (`currentUserId`,
 * mirrored by `AuthContext`) and — as a fallback for the window before that
 * mirror lands — the auth user threaded down as `selfUserId`. If BOTH are
 * unknown we must NOT guess a member: the direct-chat creator sorts first in
 * the backend's `joined_at ASC` member ordering, so guessing meant showing
 * the viewer their own name as the partner (the sidebar-name bug).
 */
export function deriveTitle(
  c: Conversation,
  currentUserId: string | null,
  fallbackUserId: string | null = null,
): string {
  if (c.name && c.name.trim()) return c.name;
  const selfId = currentUserId ?? fallbackUserId;

  if (c.type === 'direct') {
    const members = c.members ?? [];
    const partner = selfId ? members.find((m) => m.userId !== selfId) : undefined;
    if (partner?.user) {
      return partner.user.displayName || partner.user.username;
    }
    // No usable member info (no members, only me, or self is unknown so
    // every member is ambiguous): fall back to the last message's sender,
    // but only when we can prove it isn't me.
    const sender = c.lastMessage?.sender;
    if (sender && selfId && sender.id !== selfId) {
      return sender.displayName || sender.username;
    }
    return placeholderTitle(c);
  }

  // Group with no name (rare but allowed).
  if (c.type === 'group') {
    return 'Group chat';
  }

  return placeholderTitle(c);
}

/**
 * Deterministic, non-lying placeholder. The original version used the generic
 * string "Conversation", which masked the partner's identity and broke
 * deep-linking / share-by-URL. Including the conversation id lets us still
 * show *something* while keeping rows distinguishable.
 */
function placeholderTitle(c: Conversation): string {
  return `Chat ${c.id.slice(0, 6)}`;
}

function ConversationListItemImpl({ conversation, active, onSelect, selfUserId = null }: Props) {
  const currentUserId = useChatStore((s) => s.currentUserId);
  const selfId = currentUserId ?? selfUserId;
  const presence = useChatStore((s) => {
    if (conversation.type !== 'direct' || !selfId) return undefined;
    const partner = (conversation.members ?? []).find((m) => m.userId !== selfId);
    return partner ? s.presenceByUser.get(partner.userId) : undefined;
  });
  // F-006: per-conversation notification level. Defaults to 'all' for
  // conversations without an explicit row in the store.
  const notificationLevel = useChatStore((s) => s.getConversationPref(conversation.id));
  const isMuted = notificationLevel !== 'all';

  // F-006: notification-prefs popover. Opened via right-click on desktop
  // and long-press on touch (the menu itself doesn't need to know which
  // gesture triggered it).
  const [prefsOpen, setPrefsOpen] = useState(false);

  const openPrefs = useCallback(() => setPrefsOpen(true), []);
  const closePrefs = useCallback(() => setPrefsOpen(false), []);

  const title = deriveTitle(conversation, currentUserId, selfUserId);
  const preview = conversation.lastMessage?.content ?? '';
  const ts = conversation.lastMessage?.createdAt ?? conversation.updatedAt;
  const unread = conversation.unreadCount ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        // Right-click (desktop) / long-press (touch) opens the
        // notification-prefs popover. `onContextMenu` fires on long-press
        // on iOS Safari since the underlying event is still a mouse-event
        // surface, but mobile users typically tap the 🔔 icon instead — see
        // the rendered button below. `preventDefault` keeps the browser
        // context menu from appearing over ours.
        onContextMenu={(e) => {
          e.preventDefault();
          openPrefs();
        }}
        className={clsx(
          'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition',
          active ? 'bg-brand-50 dark:bg-ink-800' : 'hover:bg-ink-100 dark:hover:bg-ink-800/70',
        )}
        aria-current={active ? 'true' : undefined}
      >
        <span className="relative">
          <UserAvatar
            name={title}
            src={conversation.imageUrl}
            size={44}
          />
          {conversation.type === 'direct' && (
            <span className="absolute -bottom-0.5 -right-0.5">
              <PresenceDot online={presence?.online} lastSeen={presence?.lastSeen} />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate font-medium">{title}</span>
              {/* F-006: bell glyph next to conversations whose notification
                  level isn't 'all' (i.e. muted or mentions-only). Acts as
                  both a status indicator and a tap target on touch. */}
              {isMuted && (
                <button
                  type="button"
                  aria-label="Notification preferences"
                  title={
                    notificationLevel === 'off'
                      ? 'Notifications off'
                      : 'Mentions only'
                  }
                  data-testid="notification-prefs-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPrefs();
                  }}
                  className="shrink-0 rounded p-0.5 text-ink-500 hover:bg-ink-200 dark:text-ink-300 dark:hover:bg-ink-700"
                >
                  <span aria-hidden="true">{notificationLevel === 'off' ? '🔕' : '🔔'}</span>
                </button>
              )}
            </span>
            <span className="shrink-0 text-[11px] text-ink-500 dark:text-ink-300">{timeShort(ts)}</span>
          </span>
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-xs text-ink-500 dark:text-ink-300">{preview}</span>
            {unread > 0 && <span className="badge">{unread > 99 ? '99+' : unread}</span>}
          </span>
        </span>
      </button>
      {prefsOpen && (
        <NotificationPrefsMenu
          conversationId={conversation.id}
          open={prefsOpen}
          onClose={closePrefs}
        />
      )}
    </>
  );
}

export default memo(ConversationListItemImpl);
